<template>
  <div class="mobile-courses-page">
    <!-- Header matching Flutter AppScaffold -->
    <div class="mobile-header">
      <div class="header-content">
        <h1 class="header-title">{{ categoryTitle }}</h1>
        <button class="cart-button" @click="navigateToCart">
          <i class="fas fa-shopping-cart"></i>
        </button>
      </div>
    </div>

    <!-- Tab Bar matching Flutter tabs -->
    <div class="mobile-tabs">
      <button
        class="mobile-tab"
        :class="{ active: activeTab === 'recorded' }"
        @click="switchTab('recorded')"
      >
        {{ $t("category_courses.recorded_course") || "Recorded Course" }}
      </button>
      <button
        class="mobile-tab"
        :class="{ active: activeTab === 'live' }"
        @click="switchTab('live')"
      >
        {{ $t("category_courses.live") || "Live" }}
      </button>
      <button
        class="mobile-tab"
        :class="{ active: activeTab === 'bundles' }"
        @click="switchTab('bundles')"
      >
        {{ $t("category_courses.all_bundles") || "All Bundles" }}
      </button>
    </div>

    <!-- Search Bar matching Flutter SearchBar -->
    <div class="search-container">
      <div class="search-bar">
        <input
          v-model="searchText"
          type="text"
          class="search-input"
          :placeholder="$t('common.search') || 'Search'"
          @keyup.enter="performSearch"
        />
        <button class="search-button" @click="performSearch">
          <i class="fas fa-search"></i>
          <span>{{ $t("common.search") || "Search" }}</span>
        </button>
      </div>
    </div>

    <!-- Content Area -->
    <div class="content-area">
      <!-- Recorded Courses Tab -->
      <div v-if="activeTab === 'recorded'" class="tab-content">
        <div v-if="isLoading" class="loading-container">
          <div class="spinner"></div>
          <p>{{ $t("common.loading") || "Loading..." }}</p>
        </div>
        <div v-else-if="courses.length === 0" class="empty-state">
          <i class="fas fa-book-open"></i>
          <p>
            {{ $t("category_courses.no_courses") || "No courses available" }}
          </p>
        </div>
        <div v-else class="courses-list">
          <div
            v-for="course in courses"
            :key="course.id"
            class="course-list-item"
            @click="navigateToCourse(course)"
          >
            <!-- Image Section -->
            <div class="course-image-section">
              <div class="course-image-wrapper">
                <img
                  :src="course.image || defaultImage"
                  @error="setDefaultImage"
                  alt="Course"
                  class="course-image"
                />
                <!-- Image Actions (Add to Cart) -->
                <div
                  v-if="!course.is_purchased && course.price > 0"
                  class="image-actions"
                >
                  <button
                    class="cart-icon-button"
                    :disabled="loadingCart[course.id]"
                    @click.stop="addCourseToCart(course.id)"
                  >
                    <i
                      v-if="loadingCart[course.id]"
                      class="fas fa-spinner fa-spin"
                    ></i>
                    <i
                      v-else-if="cartCourses.includes(course.id)"
                      class="fas fa-shopping-cart"
                    ></i>
                    <i v-else class="fas fa-cart-plus"></i>
                  </button>
                </div>
              </div>
              <!-- Last Update Hint -->
              <div v-if="course.last_update" class="image-hint">
                {{ $t("category_courses.last_updated") || "Last Updated" }}
                {{ formatDate(course.last_update) }}
              </div>
            </div>

            <!-- Content Section -->
            <div class="course-content-section">
              <div class="course-info">
                <!-- Title -->
                <h3 class="course-title">{{ course.title }}</h3>
                <!-- Instructor -->
                <p v-if="course.user" class="course-instructor">
                  {{ course.user.fname }} {{ course.user.lname }}
                </p>

                <!-- Features/Badges -->
                <div class="course-features">
                  <!-- Live Badge with Animation -->
                  <span v-if="course.is_live === '1'" class="badge live-badge">
                    <span class="live-dot"></span>
                    {{ $t("category_courses.live") || "Live" }}
                  </span>
                  <!-- QBank Badge -->
                  <span
                    v-if="course.subscription_plan_id"
                    class="badge qbank-badge"
                  >
                    {{ $t("category_courses.qbank") || "QBank" }}
                  </span>
                  <!-- Institute Badge -->
                  <span v-if="course.institute" class="badge institute-badge">
                    {{ course.institute.title }}
                  </span>
                </div>
              </div>

              <!-- Bottom Section: Price/Progress + Action -->
              <div class="course-bottom">
                <!-- Progress for Purchased Courses -->
                <div v-if="course.is_purchased" class="progress-section">
                  <div class="progress-bar-container">
                    <div
                      class="progress-bar-fill"
                      :style="{ width: `${course.progress || 0}%` }"
                    ></div>
                  </div>
                  <span class="progress-text">{{ course.progress || 0 }}%</span>
                </div>

                <!-- Price for Non-Purchased Courses -->
                <div v-else class="price-section">
                  <!-- Free Course -->
                  <span
                    v-if="course.discount_price === 0 || course.price === 0"
                    class="price-free"
                  >
                    {{ $t("course.free") || "Free" }}
                  </span>
                  <!-- Discounted Price -->
                  <template
                    v-else-if="
                      course.discount_price &&
                      course.discount_price !== course.price
                    "
                  >
                    <span class="price-old">{{ course.price }}</span>
                    <span class="price-current">
                      {{ course.discount_price }} {{ $appCurrency }}
                    </span>
                  </template>
                  <!-- Regular Price -->
                  <span v-else class="price-current">
                    {{ course.price }} {{ $appCurrency }}
                  </span>
                </div>

                <!-- Action Button -->
                <button
                  class="action-button"
                  @click.stop="navigateToCourse(course)"
                >
                  <i class="fas fa-arrow-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="pagination-container">
          <button
            class="pagination-button"
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
          <button
            class="pagination-button"
            :disabled="currentPage === totalPages"
            @click="changePage(currentPage + 1)"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>

      <!-- Live Courses Tab -->
      <div v-if="activeTab === 'live'" class="tab-content">
        <div v-if="isLoading" class="loading-container">
          <div class="spinner"></div>
          <p>{{ $t("common.loading") || "Loading..." }}</p>
        </div>
        <div v-else-if="courses.length === 0" class="empty-state">
          <i class="fas fa-video"></i>
          <p>
            {{
              $t("category_courses.no_live_courses") ||
              "No live courses available"
            }}
          </p>
        </div>
        <div v-else class="courses-list">
          <div
            v-for="course in courses"
            :key="course.id"
            class="course-list-item"
            @click="navigateToCourse(course)"
          >
            <!-- Same structure as recorded courses -->
            <div class="course-image-section">
              <div class="course-image-wrapper">
                <img
                  :src="course.image || defaultImage"
                  @error="setDefaultImage"
                  alt="Course"
                  class="course-image"
                />
                <div
                  v-if="!course.is_purchased && course.price > 0"
                  class="image-actions"
                >
                  <button
                    class="cart-icon-button"
                    :disabled="loadingCart[course.id]"
                    @click.stop="addCourseToCart(course.id)"
                  >
                    <i
                      v-if="loadingCart[course.id]"
                      class="fas fa-spinner fa-spin"
                    ></i>
                    <i
                      v-else-if="cartCourses.includes(course.id)"
                      class="fas fa-shopping-cart"
                    ></i>
                    <i v-else class="fas fa-cart-plus"></i>
                  </button>
                </div>
              </div>
              <div v-if="course.last_update" class="image-hint">
                {{ $t("category_courses.last_updated") || "Last Updated" }}
                {{ formatDate(course.last_update) }}
              </div>
            </div>

            <div class="course-content-section">
              <div class="course-info">
                <h3 class="course-title">{{ course.title }}</h3>
                <p v-if="course.user" class="course-instructor">
                  {{ course.user.fname }} {{ course.user.lname }}
                </p>

                <div class="course-features">
                  <span v-if="course.is_live === '1'" class="badge live-badge">
                    <span class="live-dot"></span>
                    {{ $t("category_courses.live") || "Live" }}
                  </span>
                  <span
                    v-if="course.subscription_plan_id"
                    class="badge qbank-badge"
                  >
                    {{ $t("category_courses.qbank") || "QBank" }}
                  </span>
                  <span v-if="course.institute" class="badge institute-badge">
                    {{ course.institute.title }}
                  </span>
                </div>
              </div>

              <div class="course-bottom">
                <div v-if="course.is_purchased" class="progress-section">
                  <div class="progress-bar-container">
                    <div
                      class="progress-bar-fill"
                      :style="{ width: `${course.progress || 0}%` }"
                    ></div>
                  </div>
                  <span class="progress-text">{{ course.progress || 0 }}%</span>
                </div>

                <div v-else class="price-section">
                  <span
                    v-if="course.discount_price === 0 || course.price === 0"
                    class="price-free"
                  >
                    {{ $t("course.free") || "Free" }}
                  </span>
                  <template
                    v-else-if="
                      course.discount_price &&
                      course.discount_price !== course.price
                    "
                  >
                    <span class="price-old">{{ course.price }}</span>
                    <span class="price-current">
                      {{ course.discount_price }} {{ $appCurrency }}
                    </span>
                  </template>
                  <span v-else class="price-current">
                    {{ course.price }} {{ $appCurrency }}
                  </span>
                </div>

                <button
                  class="action-button"
                  @click.stop="navigateToCourse(course)"
                >
                  <i class="fas fa-arrow-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="totalPages > 1" class="pagination-container">
          <button
            class="pagination-button"
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
          <button
            class="pagination-button"
            :disabled="currentPage === totalPages"
            @click="changePage(currentPage + 1)"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>

      <!-- Bundles Tab -->
      <div v-if="activeTab === 'bundles'" class="tab-content">
        <div v-if="isLoadingBundles" class="loading-container">
          <div class="spinner"></div>
          <p>{{ $t("common.loading") || "Loading..." }}</p>
        </div>
        <div v-else-if="bundles.length === 0" class="empty-state">
          <i class="fas fa-box-open"></i>
          <p>
            {{ $t("category_courses.no_bundles") || "No bundles available" }}
          </p>
        </div>
        <div v-else class="courses-list">
          <div
            v-for="bundle in bundles"
            :key="bundle.id"
            class="course-list-item"
            @click="navigateToBundle(bundle)"
          >
            <div class="course-image-section">
              <div class="course-image-wrapper">
                <img
                  :src="bundle.preview_image || defaultImage"
                  @error="setDefaultImage"
                  alt="Bundle"
                  class="course-image"
                />
                <div v-if="bundle.price > 0" class="image-actions">
                  <button
                    class="cart-icon-button"
                    :disabled="loadingCart[bundle.id]"
                    @click.stop="addBundleToCart(bundle.id)"
                  >
                    <i
                      v-if="loadingCart[bundle.id]"
                      class="fas fa-spinner fa-spin"
                    ></i>
                    <i
                      v-else-if="cartBundles.includes(bundle.id)"
                      class="fas fa-shopping-cart"
                    ></i>
                    <i v-else class="fas fa-cart-plus"></i>
                  </button>
                </div>
              </div>
            </div>

            <div class="course-content-section">
              <div class="course-info">
                <h3 class="course-title">{{ bundle.title }}</h3>
                <p v-if="bundle.user" class="course-instructor">
                  {{ bundle.user }}
                </p>

                <div class="course-features">
                  <span v-if="bundle.institute" class="badge institute-badge">
                    {{ bundle.institute.title }}
                  </span>
                </div>
              </div>

              <div class="course-bottom">
                <div class="price-section">
                  <span
                    v-if="bundle.discount_price === 0 || bundle.price === 0"
                    class="price-free"
                  >
                    {{ $t("course.free") || "Free" }}
                  </span>
                  <template
                    v-else-if="
                      bundle.discount_price &&
                      bundle.discount_price !== bundle.price
                    "
                  >
                    <span class="price-old">{{ bundle.price }}</span>
                    <span class="price-current">
                      {{ bundle.discount_price }} {{ $appCurrency }}
                    </span>
                  </template>
                  <span v-else class="price-current">
                    {{ bundle.price }} {{ $appCurrency }}
                  </span>
                </div>

                <button
                  class="action-button"
                  @click.stop="navigateToBundle(bundle)"
                >
                  <i class="fas fa-arrow-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="totalBundlesPages > 1" class="pagination-container">
          <button
            class="pagination-button"
            :disabled="currentBundlePage === 1"
            @click="changeBundlePage(currentBundlePage - 1)"
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          <span class="page-info"
            >{{ currentBundlePage }} / {{ totalBundlesPages }}</span
          >
          <button
            class="pagination-button"
            :disabled="currentBundlePage === totalBundlesPages"
            @click="changeBundlePage(currentBundlePage + 1)"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiUrl } from "@/utils/api";
import Swal from "sweetalert2";

export default {
  name: "CategoryCoursesMobile",
  data() {
    return {
      activeTab: "recorded",
      searchText: "",
      currentSearchQuery: "",
      courses: [],
      bundles: [],
      isLoading: false,
      isLoadingBundles: false,
      currentPage: 1,
      totalPages: 1,
      currentBundlePage: 1,
      totalBundlesPages: 1,
      perPage: 10,
      categoryTitle: "",
      defaultImage: "/assets/img/acad-icon.png",
      token: localStorage.getItem("access_token"),
      loadingCart: {},
      cartCourses: [],
      cartBundles: [],
    };
  },
  mounted() {
    this.initializeFromRoute();
    this.loadCartItems();
  },
  methods: {
    initializeFromRoute() {
      const urlParams = new URLSearchParams(window.location.search);
      const valueId = urlParams.get("value_id");
      const value = urlParams.get("value");

      this.categoryTitle = urlParams.get("title") || "Courses";

      if (valueId && value) {
        this.fetchCourses();
      }
    },

    switchTab(tab) {
      this.activeTab = tab;
      this.searchText = "";
      this.currentSearchQuery = "";
      this.currentPage = 1;
      this.currentBundlePage = 1;

      if (tab === "recorded") {
        this.fetchCourses();
      } else if (tab === "live") {
        this.fetchCourses(true);
      } else if (tab === "bundles") {
        this.fetchBundles();
      }
    },

    performSearch() {
      this.currentSearchQuery = this.searchText;
      this.currentPage = 1;
      this.currentBundlePage = 1;

      if (this.activeTab === "bundles") {
        this.fetchBundles();
      } else {
        this.fetchCourses(this.activeTab === "live");
      }
    },

    async fetchCourses(isLive = false) {
      this.isLoading = true;
      const urlParams = new URLSearchParams(window.location.search);
      const valueId = urlParams.get("value_id");
      const value = urlParams.get("value");

      const data = {
        page: this.currentPage,
        limit: this.perPage,
      };

      if (valueId === "category_id") {
        data.category_id = value;
      } else if (valueId === "subcategory_id") {
        data.subcategory_id = value;
      } else if (valueId === "childcategory_id") {
        data.childcategory_id = value;
      }

      if (this.currentSearchQuery) {
        data.search = this.currentSearchQuery;
      }

      if (isLive) {
        data.is_live = "1";
      } else {
        data.is_live = "0";
      }

      try {
        const response = await fetch(`${apiUrl}/web/get-courses`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion.phone_code,
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify(data),
        });

        const result = await response.json();
        const resData = result.courses;

        if (resData && resData.data) {
          this.courses = resData.data;
          if (resData.meta) {
            this.totalPages = Math.ceil(
              resData.meta.total / resData.meta.per_page
            );
          }
        } else {
          this.courses = [];
          this.totalPages = 1;
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
        this.courses = [];
        this.totalPages = 1;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchBundles() {
      this.isLoadingBundles = true;
      const urlParams = new URLSearchParams(window.location.search);
      const valueId = urlParams.get("value_id");
      const value = urlParams.get("value");

      const data = {
        page: this.currentBundlePage,
        limit: this.perPage,
      };

      if (valueId === "category_id") {
        data.category_id = value;
      } else if (valueId === "subcategory_id") {
        data.subcategory_id = value;
      } else if (valueId === "childcategory_id") {
        data.childcategory_id = value;
      }

      if (this.currentSearchQuery) {
        data.search = this.currentSearchQuery;
      }

      try {
        const response = await fetch(`${apiUrl}/web/get-bundles`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion.phone_code,
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify(data),
        });

        const result = await response.json();
        const resData = result.bundle;

        if (resData && resData.data) {
          this.bundles = resData.data;
          if (resData.meta) {
            this.totalBundlesPages = Math.ceil(
              resData.meta.total / resData.meta.per_page
            );
          }
        } else if (Array.isArray(resData)) {
          this.bundles = resData;
        } else {
          this.bundles = [];
          this.totalBundlesPages = 1;
        }
      } catch (error) {
        console.error("Error fetching bundles:", error);
        this.bundles = [];
        this.totalBundlesPages = 1;
      } finally {
        this.isLoadingBundles = false;
      }
    },

    changePage(page) {
      this.currentPage = page;
      this.fetchCourses(this.activeTab === "live");
    },

    changeBundlePage(page) {
      this.currentBundlePage = page;
      this.fetchBundles();
    },

    async addCourseToCart(courseId) {
      if (!this.token) {
        Swal.fire({
          icon: "warning",
          title: this.$t("common.login_required") || "Please login first",
          confirmButtonText: this.$t("common.ok") || "OK",
        });
        return;
      }

      this.$set(this.loadingCart, courseId, true);

      try {
        const response = await fetch(`${apiUrl}/add/cart`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion.phone_code,
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({
            course_id: courseId,
            secret: "a463c0cd-78ed-4bda-a5f6-f70c3e597dfb",
          }),
        });

        const result = await response.json();

        if (response.ok) {
          this.cartCourses.push(courseId);
          Swal.fire({
            icon: "success",
            title:
              this.$t("category_courses.course_added_to_cart") ||
              "Course added to cart",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 2000,
          });
        } else {
          const errorMsg = (result.message || "").toLowerCase();
          if (
            errorMsg.includes("already in cart") ||
            errorMsg.includes("already exists")
          ) {
            if (!this.cartCourses.includes(courseId))
              this.cartCourses.push(courseId);
            Swal.fire({
              icon: "info",
              title:
                this.$t("cart.already_in_cart") ||
                "This item is already in your cart.",
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 2000,
            });
          } else {
            throw new Error(result.message || "Failed to add to cart");
          }
        }
      } catch (error) {
        console.error("Error adding to cart:", error);
        Swal.fire({
          icon: "error",
          title:
            this.$t("category_courses.cart_add_failed") ||
            "Failed to add to cart",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
        });
      } finally {
        this.$set(this.loadingCart, courseId, false);
      }
    },

    async addBundleToCart(bundleId) {
      if (!this.token) {
        Swal.fire({
          icon: "warning",
          title: this.$t("common.login_required") || "Please login first",
          confirmButtonText: this.$t("common.ok") || "OK",
        });
        return;
      }

      this.$set(this.loadingCart, bundleId, true);

      try {
        const response = await fetch(`${apiUrl}/add/cart`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion.phone_code,
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({
            bundle_id: bundleId,
            secret: "a463c0cd-78ed-4bda-a5f6-f70c3e597dfb",
          }),
        });

        const result = await response.json();

        if (response.ok) {
          this.cartBundles.push(bundleId);
          Swal.fire({
            icon: "success",
            title:
              this.$t("category_courses.bundle_added_to_cart") ||
              "Bundle added to cart",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 2000,
          });
        } else {
          const errorMsg = (result.message || "").toLowerCase();
          if (
            errorMsg.includes("already in cart") ||
            errorMsg.includes("already exists")
          ) {
            if (!this.cartBundles.includes(bundleId))
              this.cartBundles.push(bundleId);
            Swal.fire({
              icon: "info",
              title:
                this.$t("cart.already_in_cart") ||
                "This item is already in your cart.",
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 2000,
            });
          } else {
            throw new Error(result.message || "Failed to add to cart");
          }
        }
      } catch (error) {
        console.error("Error adding bundle to cart:", error);
        Swal.fire({
          icon: "error",
          title:
            this.$t("category_courses.cart_add_failed") ||
            "Failed to add to cart",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
        });
      } finally {
        this.$set(this.loadingCart, bundleId, false);
      }
    },

    async loadCartItems() {
      if (!this.token) return;

      try {
        const response = await fetch(
          `${apiUrl}/show/cart?secret=a463c0cd-78ed-4bda-a5f6-f70c3e597dfb`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${this.token}`,
            },
          }
        );

        const data = await response.json();
        if (data.cart && Array.isArray(data.cart)) {
          this.cartCourses = data.cart
            .filter((item) => item.course_id !== null)
            .map((item) => item.course_id);
          this.cartBundles = data.cart
            .filter((item) => item.bundle_id !== null)
            .map((item) => item.bundle_id);
        }
      } catch (error) {
        console.error("Error loading cart:", error);
      }
    },

    navigateToCourse(course) {
      window.location.href = `/course-details?id=${course.id}`;
    },

    navigateToBundle(bundle) {
      window.location.href = `/bundle-details?id=${bundle.id}`;
    },

    navigateToCart() {
      window.location.href = "/Cart";
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString(this.$i18n.locale || "en", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    setDefaultImage(event) {
      event.target.src = this.defaultImage;
    },
  },
};
</script>

<style scoped>
/* ===== Mobile-First Design matching Flutter ===== */
.mobile-courses-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 20px;
}

/* ===== Header (AppBar equivalent) ===== */
.mobile-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
}

.header-title {
  color: white;
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

.cart-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: background 0.3s ease;
}

.cart-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

.cart-button i {
  font-size: 18px;
}

/* ===== Tabs (TabBar equivalent) ===== */
.mobile-tabs {
  display: flex;
  background: white;
  padding: 8px;
  gap: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow-x: auto;
}

.mobile-tab {
  flex: 1;
  min-width: 120px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.mobile-tab:hover {
  background: #f0f0f0;
  color: #00c744;
}

.mobile-tab.active {
  background: #00c744;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 199, 68, 0.3);
}

/* ===== Search Bar (Flutter SearchBar equivalent) ===== */
.search-container {
  padding: 8px 16px;
  background: white;
}

.search-bar {
  display: flex;
  gap: 8px;
  align-items: center;
  background: #f5f5f5;
  border-radius: 12px;
  padding: 4px;
  max-width: 1200px;
  margin: 0 auto;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 12px 16px;
  font-size: 0.95rem;
  outline: none;
}

.search-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #00c744;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;
}

.search-button:hover {
  background: #00a83b;
}

.search-button i {
  font-size: 14px;
}

/* ===== Content Area ===== */
.content-area {
  padding: 8px;
  max-width: 1200px;
  margin: 0 auto;
}

.tab-content {
  min-height: 400px;
}

/* ===== Loading State ===== */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #00c744;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-container p {
  margin-top: 16px;
  color: #666;
  font-weight: 600;
}

/* ===== Empty State ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-state i {
  font-size: 60px;
  color: #ccc;
  margin-bottom: 16px;
}

.empty-state p {
  color: #666;
  font-size: 1rem;
  font-weight: 600;
}

/* ===== Course List (ListView equivalent) ===== */
.courses-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ===== Course List Item (ListItem1 equivalent) ===== */
.course-list-item {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 8px;
  display: flex;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.course-list-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #00c744;
}

/* ===== Image Section ===== */
.course-image-section {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.course-image-wrapper {
  position: relative;
  width: 120px;
  height: 120px;
}

.course-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 16px;
}

/* ===== Image Actions (overlaid on image) ===== */
.image-actions {
  position: absolute;
  bottom: 4px;
  left: 4px;
  right: 4px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.cart-icon-button {
  background: transparent;
  border: none;
  color: #00c744;
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  transition: color 0.3s ease;
}

.cart-icon-button:hover:not(:disabled) {
  color: #00a83b;
}

.cart-icon-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ===== Image Hint (Last Update) ===== */
.image-hint {
  font-size: 0.7rem;
  color: #999;
  text-align: center;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ===== Content Section ===== */
.course-content-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
}

.course-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.course-title {
  font-size: 1rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;
}

.course-instructor {
  font-size: 0.85rem;
  color: #666;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ===== Features/Badges ===== */
.course-features {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 8px;
  overflow-x: auto;
  scrollbar-width: none;
}

.course-features::-webkit-scrollbar {
  display: none;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

/* Live Badge with animated dot */
.live-badge {
  background: #ef4444;
  color: white;
}

.live-dot {
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  animation: pulse-dot 1s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

.qbank-badge {
  background: #3b82f6;
  color: white;
}

.institute-badge {
  background: #e8fff1;
  color: #00a83b;
}

/* ===== Bottom Section (Price/Progress + Action) ===== */
.course-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
}

/* Progress Section (for purchased courses) */
.progress-section {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-bar-container {
  flex: 1;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: #00c744;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.8rem;
  color: #666;
  font-weight: 600;
  min-width: 40px;
  text-align: right;
}

/* Price Section */
.price-section {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.price-free {
  font-size: 1rem;
  font-weight: 700;
  color: #00c744;
}

.price-current {
  font-size: 1rem;
  font-weight: 700;
  color: #00c744;
}

.price-old {
  font-size: 0.8rem;
  color: #999;
  text-decoration: line-through;
}

/* Action Button (IconButton.filledTonal equivalent) */
.action-button {
  width: 32px;
  height: 32px;
  min-width: 32px;
  background: #e8fff1;
  color: #00c744;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.action-button:hover {
  background: #00c744;
  color: white;
  transform: scale(1.1);
}

.action-button i {
  font-size: 12px;
}

/* ===== Pagination ===== */
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px;
  margin-top: 16px;
}

.pagination-button {
  width: 40px;
  height: 40px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00c744;
  cursor: pointer;
  transition: all 0.3s ease;
}

.pagination-button:hover:not(:disabled) {
  background: #00c744;
  color: white;
  border-color: #00c744;
}

.pagination-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.page-info {
  font-size: 0.9rem;
  font-weight: 600;
  color: #666;
}

/* ===== Responsive Design ===== */
@media (max-width: 768px) {
  .course-list-item {
    padding: 6px;
    gap: 8px;
  }

  .course-image-wrapper {
    width: 100px;
    height: 100px;
  }

  .image-hint {
    max-width: 100px;
    font-size: 0.65rem;
  }

  .course-title {
    font-size: 0.9rem;
  }

  .course-instructor {
    font-size: 0.75rem;
  }

  .badge {
    font-size: 0.7rem;
    padding: 3px 6px;
  }

  .mobile-tab {
    font-size: 0.85rem;
    padding: 10px 12px;
    min-width: 100px;
  }

  .search-button span {
    display: none;
  }

  .search-button {
    padding: 10px 16px;
  }
}

@media (min-width: 769px) {
  .content-area {
    padding: 16px;
  }

  .courses-list {
    gap: 12px;
  }

  .course-list-item {
    padding: 12px;
  }
}

/* ===== Dark Mode Support ===== */
[data-bs-theme="dark"] .mobile-courses-page,
[data-theme="dark"] .mobile-courses-page,
.dark .mobile-courses-page {
  background: #1a1a1a;
}

[data-bs-theme="dark"] .mobile-tabs,
[data-theme="dark"] .mobile-tabs,
.dark .mobile-tabs {
  background: #2d2d2d;
}

[data-bs-theme="dark"] .mobile-tab,
[data-theme="dark"] .mobile-tab,
.dark .mobile-tab {
  color: #999;
}

[data-bs-theme="dark"] .mobile-tab:hover,
[data-theme="dark"] .mobile-tab:hover,
.dark .mobile-tab:hover {
  background: #3d3d3d;
}

[data-bs-theme="dark"] .search-container,
[data-theme="dark"] .search-container,
.dark .search-container {
  background: #2d2d2d;
}

[data-bs-theme="dark"] .search-bar,
[data-theme="dark"] .search-bar,
.dark .search-bar {
  background: #3d3d3d;
}

[data-bs-theme="dark"] .search-input,
[data-theme="dark"] .search-input,
.dark .search-input {
  color: white;
}

[data-bs-theme="dark"] .course-list-item,
[data-theme="dark"] .course-list-item,
.dark .course-list-item {
  background: #2d2d2d;
  border-color: #3d3d3d;
}

[data-bs-theme="dark"] .course-title,
[data-theme="dark"] .course-title,
.dark .course-title {
  color: #f5f5f5;
}

[data-bs-theme="dark"] .course-instructor,
[data-theme="dark"] .course-instructor,
.dark .course-instructor {
  color: #999;
}

[data-bs-theme="dark"] .progress-bar-container,
[data-theme="dark"] .progress-bar-container,
.dark .progress-bar-container {
  background: #3d3d3d;
}

[data-bs-theme="dark"] .action-button,
[data-theme="dark"] .action-button,
.dark .action-button {
  background: #3d3d3d;
}

[data-bs-theme="dark"] .pagination-button,
[data-theme="dark"] .pagination-button,
.dark .pagination-button {
  background: #2d2d2d;
  border-color: #3d3d3d;
}

[data-bs-theme="dark"] .image-actions,
[data-theme="dark"] .image-actions,
.dark .image-actions {
  background: rgba(45, 45, 45, 0.85);
}
</style>
